/**
import logger from '../lib/logger';
 * SpendingInsightsDashboard - AI-powered spending analytics with forecasting
 */
import { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  getAIForecast,
  getSpendingComparison,
  getHealthScore,
  getCashFlowForecast,
  getSavingsRecommendations
} from '../lib/api';
import { toast } from 'sonner';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  Brain,
  Target,
  Sparkles,
  Calendar,
  DollarSign,
  PiggyBank,
  AlertTriangle,
  CheckCircle2,
  ArrowUpRight,
  ArrowDownRight,
  Loader2,
  RefreshCw,
  Lightbulb,
  Shield,
  Zap,
  BarChart3,
  LineChart
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  ComposedChart,
  Line
} from 'recharts';

const CATEGORY_COLORS = {
  'Shopping': '#10b981',
  'Dining': '#f59e0b',
  'Transport': '#3b82f6',
  'Entertainment': '#8b5cf6',
  'Utilities': '#ef4444',
  'Healthcare': '#ec4899',
  'Travel': '#06b6d4',
  'Other': '#6b7280'
};

const SpendingInsightsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [forecast, setForecast] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [healthScore, setHealthScore] = useState(null);
  const [cashFlow, setCashFlow] = useState(null);
  const [recommendations, setRecommendations] = useState([]);

  const fetchAllData = useCallback(async () => {
    try {
      const [forecastRes, comparisonRes, healthRes, cashFlowRes, recsRes] = await Promise.all([
        getAIForecast().catch(() => ({ data: null })),
        getSpendingComparison().catch(() => ({ data: null })),
        getHealthScore().catch(() => ({ data: null })),
        getCashFlowForecast(14).catch(() => ({ data: [] })),
        getSavingsRecommendations().catch(() => ({ data: [] }))
      ]);

      setForecast(forecastRes.data);
      setComparison(comparisonRes.data);
      setHealthScore(healthRes.data);
      setCashFlow(cashFlowRes.data);
      setRecommendations(recsRes.data || []);
    } catch (error) {
      logger.error('Error fetching data:', error);
      toast.error('Failed to load some insights');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllData();
    setRefreshing(false);
    toast.success('Insights refreshed');
  };

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const getTrendIcon = (direction) => {
    if (direction === 'up' || direction === 'increasing') return <TrendingUp className="w-4 h-4 text-red-500" />;
    if (direction === 'down' || direction === 'decreasing') return <TrendingDown className="w-4 h-4 text-emerald-500" />;
    return <Minus className="w-4 h-4 text-gray-500" />;
  };

  const getChangeColor = (value) => {
    if (value > 0) return 'text-red-500';
    if (value < 0) return 'text-emerald-500';
    return 'text-gray-500';
  };

  const getRiskColor = (level) => {
    if (level === 'high') return 'bg-red-500';
    if (level === 'medium') return 'bg-amber-500';
    return 'bg-emerald-500';
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center space-y-4">
            <Loader2 className="w-12 h-12 animate-spin text-emerald-500 mx-auto" />
            <p className="text-muted-foreground">Analyzing your spending patterns...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <motion.div
        className="space-y-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="spending-insights-dashboard"
      >
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-heading font-bold">Spending Insights</h1>
              <Badge className="bg-purple-500/10 text-purple-600 border-purple-500/20">
                <Brain className="w-3 h-3 mr-1" />
                AI-Powered
              </Badge>
            </div>
            <p className="text-muted-foreground">Predictive analytics & personalized recommendations</p>
          </div>
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={refreshing}
            className="rounded-xl"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Quick Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Health Score */}
          <Card className="border-l-4 border-l-emerald-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Health Score</p>
                  <p className="text-3xl font-heading font-bold">{healthScore?.score || 0}</p>
                </div>
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  (healthScore?.score || 0) >= 70 ? 'bg-emerald-500/10' : 
                  (healthScore?.score || 0) >= 50 ? 'bg-amber-500/10' : 'bg-red-500/10'
                }`}>
                  <Shield className={`w-6 h-6 ${
                    (healthScore?.score || 0) >= 70 ? 'text-emerald-500' : 
                    (healthScore?.score || 0) >= 50 ? 'text-amber-500' : 'text-red-500'
                  }`} />
                </div>
              </div>
              <Progress value={healthScore?.score || 0} className="mt-3 h-2" />
            </CardContent>
          </Card>

          {/* Week Forecast */}
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Next Week Forecast</p>
                  <p className="text-3xl font-heading font-bold">
                    ${forecast?.next_week_forecast?.predicted_total?.toFixed(0) || '0'}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-blue-500" />
                </div>
              </div>
              <div className="flex items-center gap-2 mt-2">
                {getTrendIcon(forecast?.next_week_forecast?.trend)}
                <span className="text-xs text-muted-foreground capitalize">
                  {forecast?.next_week_forecast?.trend || 'stable'} trend
                </span>
                <Badge variant="outline" className="text-xs ml-auto">
                  {((forecast?.next_week_forecast?.confidence || 0) * 100).toFixed(0)}% confident
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Month Forecast */}
          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Next Month Forecast</p>
                  <p className="text-3xl font-heading font-bold">
                    ${forecast?.next_month_forecast?.predicted_total?.toFixed(0) || '0'}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center">
                  <LineChart className="w-6 h-6 text-purple-500" />
                </div>
              </div>
              <div className="flex items-center gap-2 mt-2">
                {getTrendIcon(forecast?.next_month_forecast?.trend)}
                <span className="text-xs text-muted-foreground capitalize">
                  {forecast?.next_month_forecast?.trend || 'stable'} trend
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Savings Potential */}
          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Monthly Savings Potential</p>
                  <p className="text-3xl font-heading font-bold text-emerald-600">
                    ${forecast?.savings_potential?.monthly?.toFixed(0) || '0'}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
                  <PiggyBank className="w-6 h-6 text-amber-500" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                ~${forecast?.savings_potential?.yearly?.toFixed(0) || '0'}/year
              </p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="forecast" className="w-full">
          <TabsList className="grid w-full grid-cols-4 h-12 rounded-xl mb-6">
            <TabsTrigger value="forecast" className="rounded-lg">
              <Brain className="w-4 h-4 mr-2" />AI Forecast
            </TabsTrigger>
            <TabsTrigger value="comparison" className="rounded-lg">
              <BarChart3 className="w-4 h-4 mr-2" />Comparisons
            </TabsTrigger>
            <TabsTrigger value="cashflow" className="rounded-lg">
              <LineChart className="w-4 h-4 mr-2" />Cash Flow
            </TabsTrigger>
            <TabsTrigger value="recommendations" className="rounded-lg">
              <Lightbulb className="w-4 h-4 mr-2" />Recommendations
            </TabsTrigger>
          </TabsList>

          {/* AI Forecast Tab */}
          <TabsContent value="forecast" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Category Forecast Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-purple-500" />
                    Next Week by Category
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {forecast?.next_week_forecast?.category_breakdown && (
                    <div className="space-y-3">
                      {Object.entries(forecast.next_week_forecast.category_breakdown)
                        .sort(([,a], [,b]) => b - a)
                        .slice(0, 6)
                        .map(([category, amount]) => (
                          <div key={category} className="space-y-1">
                            <div className="flex items-center justify-between">
                              <span className="text-sm">{category}</span>
                              <span className="font-mono font-semibold">${amount.toFixed(2)}</span>
                            </div>
                            <Progress 
                              value={(amount / (forecast.next_week_forecast.predicted_total || 1)) * 100} 
                              className="h-2"
                              style={{ '--progress-foreground': CATEGORY_COLORS[category] || '#6b7280' }}
                            />
                          </div>
                        ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* AI Insights */}
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Brain className="w-5 h-5 text-blue-500" />
                    AI Insights
                  </CardTitle>
                  <CardDescription>
                    Generated {forecast?.generated_by === 'ai' ? 'by GPT' : 'using patterns'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {forecast?.insights?.map((insight, idx) => (
                      <div key={`insight-${idx}`} className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                        <Lightbulb className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                        <p className="text-sm">{insight}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Risk Assessment */}
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Shield className="w-5 h-5 text-emerald-500" />
                    Risk Assessment
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4 mb-4">
                    <div className={`w-16 h-16 rounded-full ${getRiskColor(forecast?.risk_assessment?.level)} flex items-center justify-center`}>
                      <span className="text-2xl font-bold text-white capitalize">
                        {(forecast?.risk_assessment?.level || 'low')[0].toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="font-semibold capitalize">{forecast?.risk_assessment?.level || 'Low'} Risk</p>
                      <p className="text-sm text-muted-foreground">Based on your spending patterns</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Contributing Factors:</p>
                    {forecast?.risk_assessment?.factors?.map((factor, idx) => (
                      <div key={`factor-${idx}`} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground" />
                        {factor}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Savings Strategies */}
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <PiggyBank className="w-5 h-5 text-emerald-500" />
                    Savings Strategies
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {forecast?.savings_potential?.strategies?.map((strategy, idx) => (
                      <div key={`strategy-${idx}`} className="flex items-start gap-3 p-3 rounded-lg border">
                        <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <p className="text-sm">{strategy}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Comparison Tab */}
          <TabsContent value="comparison" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Week-over-Week */}
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading text-lg">Week over Week</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-sm text-muted-foreground">This Week</p>
                      <p className="text-2xl font-bold">
                        ${comparison?.week_over_week?.this_week_total?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                    <div className={`flex items-center gap-1 px-3 py-1 rounded-full ${
                      (comparison?.week_over_week?.change_percent || 0) > 0 
                        ? 'bg-red-500/10 text-red-500' 
                        : 'bg-emerald-500/10 text-emerald-500'
                    }`}>
                      {(comparison?.week_over_week?.change_percent || 0) > 0 
                        ? <ArrowUpRight className="w-4 h-4" />
                        : <ArrowDownRight className="w-4 h-4" />
                      }
                      <span className="font-semibold">
                        {Math.abs(comparison?.week_over_week?.change_percent || 0).toFixed(1)}%
                      </span>
                    </div>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Last Week: ${comparison?.week_over_week?.last_week_total?.toFixed(2) || '0.00'}
                  </div>
                </CardContent>
              </Card>

              {/* Month-over-Month */}
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading text-lg">Month over Month</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-sm text-muted-foreground">This Month</p>
                      <p className="text-2xl font-bold">
                        ${comparison?.month_over_month?.this_month_total?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                    <div className={`flex items-center gap-1 px-3 py-1 rounded-full ${
                      (comparison?.month_over_month?.change_percent || 0) > 0 
                        ? 'bg-red-500/10 text-red-500' 
                        : 'bg-emerald-500/10 text-emerald-500'
                    }`}>
                      {(comparison?.month_over_month?.change_percent || 0) > 0 
                        ? <ArrowUpRight className="w-4 h-4" />
                        : <ArrowDownRight className="w-4 h-4" />
                      }
                      <span className="font-semibold">
                        {Math.abs(comparison?.month_over_month?.change_percent || 0).toFixed(1)}%
                      </span>
                    </div>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Last Month: ${comparison?.month_over_month?.last_month_total?.toFixed(2) || '0.00'}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Category Comparisons */}
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-lg">Category Comparisons</CardTitle>
                <CardDescription>How your spending has changed by category</CardDescription>
              </CardHeader>
              <CardContent>
                {comparison?.category_comparisons && comparison.category_comparisons.length > 0 ? (
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart 
                        data={comparison.category_comparisons.slice(0, 6)} 
                        layout="vertical"
                        margin={{ left: 80 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                        <XAxis type="number" tickFormatter={(v) => `$${v}`} />
                        <YAxis type="category" dataKey="category" tick={{ fontSize: 12 }} />
                        <Tooltip 
                          formatter={(value) => [`$${value.toFixed(2)}`, '']}
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                        />
                        <Legend />
                        <Bar dataKey="last_month" name="Last Month" fill="#94a3b8" radius={[0, 4, 4, 0]} />
                        <Bar dataKey="this_month" name="This Month" fill="#10b981" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    No category comparison data available
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Cash Flow Tab */}
          <TabsContent value="cashflow" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <LineChart className="w-5 h-5 text-blue-500" />
                  14-Day Cash Flow Forecast
                </CardTitle>
                <CardDescription>Projected balance based on your spending patterns</CardDescription>
              </CardHeader>
              <CardContent>
                {cashFlow && cashFlow.length > 0 ? (
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={cashFlow}>
                        <defs>
                          <linearGradient id="colorBalance" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="date" 
                          tick={{ fontSize: 11 }}
                          tickFormatter={(v) => v.slice(5)}
                        />
                        <YAxis 
                          tick={{ fontSize: 11 }}
                          tickFormatter={(v) => `$${v}`}
                        />
                        <Tooltip 
                          formatter={(value, name) => [
                            `$${value.toFixed(2)}`, 
                            name === 'projected_balance' ? 'Balance' : 
                            name === 'expected_income' ? 'Expected Income' : 'Expected Expenses'
                          ]}
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                        />
                        <Legend />
                        <Area 
                          type="monotone" 
                          dataKey="projected_balance" 
                          name="Projected Balance"
                          stroke="#10b981" 
                          strokeWidth={2}
                          fill="url(#colorBalance)" 
                        />
                        <Line 
                          type="monotone" 
                          dataKey="expected_income" 
                          name="Expected Income"
                          stroke="#3b82f6" 
                          strokeWidth={2}
                          dot={false}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="expected_expenses" 
                          name="Expected Expenses"
                          stroke="#ef4444" 
                          strokeWidth={2}
                          dot={false}
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    No cash flow data available. Make some transactions to see forecasts.
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Confidence Indicators */}
            {cashFlow && cashFlow.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading text-lg">Forecast Confidence</CardTitle>
                  <CardDescription>How confident we are in each day's prediction</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-7 gap-2">
                    {cashFlow.slice(0, 14).map((day, idx) => (
                      <div key={day.date} className="text-center">
                        <div 
                          className="w-full h-16 rounded-lg flex items-end justify-center pb-1"
                          style={{ 
                            backgroundColor: `rgba(16, 185, 129, ${day.confidence})`,
                          }}
                        >
                          <span className="text-xs font-mono text-white">
                            {(day.confidence * 100).toFixed(0)}%
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(day.date).toLocaleDateString('en', { weekday: 'short' })}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Recommendations Tab */}
          <TabsContent value="recommendations" className="space-y-6">
            {/* AI Recommendations from Forecast */}
            {forecast?.recommendations && forecast.recommendations.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Brain className="w-5 h-5 text-purple-500" />
                    AI Recommendations
                  </CardTitle>
                  <CardDescription>Personalized suggestions based on your spending patterns</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {forecast.recommendations.map((rec, idx) => (
                      <div 
                        key={`ai-rec-${idx}`} 
                        className="p-4 rounded-xl border hover:border-emerald-500/50 transition-colors"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-8 h-8 rounded-lg flex items-center justify-center"
                              style={{ backgroundColor: `${CATEGORY_COLORS[rec.category] || '#6b7280'}20` }}
                            >
                              <DollarSign 
                                className="w-4 h-4"
                                style={{ color: CATEGORY_COLORS[rec.category] || '#6b7280' }}
                              />
                            </div>
                            <span className="font-semibold">{rec.category}</span>
                          </div>
                          <Badge 
                            variant="outline" 
                            className={`capitalize ${
                              rec.action === 'reduce' ? 'border-red-500/30 text-red-600' :
                              rec.action === 'invest' ? 'border-emerald-500/30 text-emerald-600' :
                              'border-blue-500/30 text-blue-600'
                            }`}
                          >
                            {rec.action}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">{rec.reason}</p>
                        {rec.potential_savings > 0 && (
                          <div className="flex items-center gap-2 text-sm text-emerald-600">
                            <PiggyBank className="w-4 h-4" />
                            Potential savings: ${rec.potential_savings.toFixed(2)}/month
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Savings Recommendations */}
            {recommendations && recommendations.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Target className="w-5 h-5 text-blue-500" />
                    Budget Recommendations
                  </CardTitle>
                  <CardDescription>Suggested budgets based on your spending history</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {recommendations.slice(0, 5).map((rec) => (
                      <div 
                        key={rec.id} 
                        className="p-4 rounded-xl border"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-8 h-8 rounded-lg flex items-center justify-center"
                              style={{ backgroundColor: `${CATEGORY_COLORS[rec.category] || '#6b7280'}20` }}
                            >
                              <DollarSign 
                                className="w-4 h-4"
                                style={{ color: CATEGORY_COLORS[rec.category] || '#6b7280' }}
                              />
                            </div>
                            <span className="font-semibold">{rec.category}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="capitalize">
                              {rec.difficulty}
                            </Badge>
                            <Badge 
                              className={`capitalize ${
                                rec.impact === 'high' ? 'bg-emerald-500' :
                                rec.impact === 'medium' ? 'bg-amber-500' :
                                'bg-gray-500'
                              }`}
                            >
                              {rec.impact} impact
                            </Badge>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-4 mb-3">
                          <div>
                            <p className="text-xs text-muted-foreground">Current</p>
                            <p className="font-mono font-semibold">${rec.current_spending.toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Recommended</p>
                            <p className="font-mono font-semibold text-blue-600">${rec.recommended_budget.toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Savings</p>
                            <p className="font-mono font-semibold text-emerald-600">${rec.potential_savings.toFixed(2)}</p>
                          </div>
                        </div>
                        {rec.tips && rec.tips.length > 0 && (
                          <div className="space-y-1">
                            {rec.tips.slice(0, 2).map((tip, tidx) => (
                              <div key={`tip-${tidx}`} className="flex items-center gap-2 text-xs text-muted-foreground">
                                <Zap className="w-3 h-3 text-amber-500" />
                                {tip}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Health Score Breakdown */}
            {healthScore?.breakdown && (
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Shield className="w-5 h-5 text-emerald-500" />
                    Health Score Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Object.entries(healthScore.breakdown).map(([key, data]) => (
                      <div key={key} className="p-4 rounded-xl bg-muted/50 text-center">
                        <div className={`w-12 h-12 rounded-full mx-auto mb-2 flex items-center justify-center ${
                          data.status === 'good' ? 'bg-emerald-500/10' :
                          data.status === 'warning' ? 'bg-amber-500/10' :
                          'bg-red-500/10'
                        }`}>
                          {data.status === 'good' ? (
                            <CheckCircle2 className="w-6 h-6 text-emerald-500" />
                          ) : data.status === 'warning' ? (
                            <AlertTriangle className="w-6 h-6 text-amber-500" />
                          ) : (
                            <AlertTriangle className="w-6 h-6 text-red-500" />
                          )}
                        </div>
                        <p className="text-sm font-medium capitalize">
                          {key.replace(/_/g, ' ')}
                        </p>
                        <p className="text-2xl font-bold">{data.score}/{data.max}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </motion.div>
    </Layout>
  );
};

export default SpendingInsightsDashboard;
